import { apiClient } from './api';

const ZONES_ENDPOINT = '/zones';

export const zonesService = {
  // Listar zonas
  async getZones(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.map_id) queryParams.append('map_id', params.map_id);
    if (params.floor) queryParams.append('floor', params.floor);
    if (params.zone_type) queryParams.append('zone_type', params.zone_type);
    if (params.building) queryParams.append('building', params.building);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `${ZONES_ENDPOINT}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Obter zona específica
  async getZoneById(zoneId) {
    const response = await apiClient.get(`${ZONES_ENDPOINT}/${zoneId}`);
    return response.data;
  },

  // Criar zona
  async createZone(zoneData) {
    const response = await apiClient.post(ZONES_ENDPOINT, zoneData);
    return response.data;
  },

  // Atualizar zona
  async updateZone(zoneId, zoneData) {
    const response = await apiClient.put(`${ZONES_ENDPOINT}/${zoneId}`, zoneData);
    return response.data;
  },

  // Excluir zona
  async deleteZone(zoneId) {
    const response = await apiClient.delete(`${ZONES_ENDPOINT}/${zoneId}`);
    return response.data;
  },

  // Obter entidades na zona
  async getZoneEntities(zoneId) {
    const response = await apiClient.get(`${ZONES_ENDPOINT}/${zoneId}/entities`);
    return response.data;
  },

  // Obter entidades por andar
  async getEntitiesInFloor(floor) {
    const response = await apiClient.get(`${ZONES_ENDPOINT}/entities?floor=${floor}`);
    return response.data;
  },

  // Obter ocupação da zona
  async getZoneOccupancy(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.floor) queryParams.append('floor', params.floor);
    if (params.zone_id) queryParams.append('zone_id', params.zone_id);
    
    const url = `${ZONES_ENDPOINT}/occupancy${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Obter alertas da zona
  async getZoneAlerts(zoneId, params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `${ZONES_ENDPOINT}/${zoneId}/alerts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Obter alertas ativos
  async getActiveAlerts(params = {}) {
    const queryParams = new URLSearchParams();
    queryParams.append('status', 'active');
    if (params.floor) queryParams.append('floor', params.floor);
    if (params.severity) queryParams.append('severity', params.severity);
    
    const url = `${ZONES_ENDPOINT}/alerts?${queryParams.toString()}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Obter movimentações recentes
  async getRecentMovements(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.floor) queryParams.append('floor', params.floor);
    if (params.limit) queryParams.append('limit', params.limit || 20);
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    
    const url = `${ZONES_ENDPOINT}/movements?${queryParams.toString()}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Regras de alerta
  async getZoneAlertRules(zoneId) {
    const response = await apiClient.get(`${ZONES_ENDPOINT}/${zoneId}/alert-rules`);
    return response.data;
  },

  async createAlertRule(zoneId, ruleData) {
    const response = await apiClient.post(`${ZONES_ENDPOINT}/${zoneId}/alert-rules`, ruleData);
    return response.data;
  },

  async updateAlertRule(ruleId, ruleData) {
    const response = await apiClient.put(`${ZONES_ENDPOINT}/alert-rules/${ruleId}`, ruleData);
    return response.data;
  },

  async deleteAlertRule(ruleId) {
    const response = await apiClient.delete(`${ZONES_ENDPOINT}/alert-rules/${ruleId}`);
    return response.data;
  },

  // Contatos de zona
  async getZoneContacts(zoneId) {
    const response = await apiClient.get(`${ZONES_ENDPOINT}/${zoneId}/contacts`);
    return response.data;
  },

  async createZoneContact(zoneId, contactData) {
    const response = await apiClient.post(`${ZONES_ENDPOINT}/${zoneId}/contacts`, contactData);
    return response.data;
  },

  async updateZoneContact(contactId, contactData) {
    const response = await apiClient.put(`${ZONES_ENDPOINT}/contacts/${contactId}`, contactData);
    return response.data;
  },

  async deleteZoneContact(contactId) {
    const response = await apiClient.delete(`${ZONES_ENDPOINT}/contacts/${contactId}`);
    return response.data;
  },

  // Detectar zona para coordenadas
  async detectZone(locationData) {
    const response = await apiClient.post(`${ZONES_ENDPOINT}/detect`, locationData);
    return response.data;
  },

  // Utilitários para tipos de zona
  getZoneTypes() {
    return [
      { value: 'room', label: 'Quarto', color: '#4CAF50' },
      { value: 'bed', label: 'Leito', color: '#2196F3' },
      { value: 'ward', label: 'Ala', color: '#FF9800' },
      { value: 'department', label: 'Departamento', color: '#9C27B0' },
      { value: 'restricted', label: 'Restrita', color: '#F44336' },
      { value: 'corridor', label: 'Corredor', color: '#607D8B' },
      { value: 'elevator', label: 'Elevador', color: '#795548' },
      { value: 'exit', label: 'Saída', color: '#E91E63' }
    ];
  },

  getAccessLevels() {
    return [
      { value: 'public', label: 'Público' },
      { value: 'restricted', label: 'Restrito' },
      { value: 'private', label: 'Privado' },
      { value: 'emergency', label: 'Emergência' }
    ];
  },

  // Algoritmos geométricos básicos
  isPointInPolygon(point, polygon) {
    const { x, y } = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  },

  isPointInCircle(point, circle) {
    const { x, y } = point;
    const { x: cx, y: cy, radius } = circle;
    const distance = Math.sqrt(Math.pow(x - cx, 2) + Math.pow(y - cy, 2));
    return distance <= radius;
  },

  calculatePolygonArea(polygon) {
    let area = 0;
    const n = polygon.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += polygon[i].x * polygon[j].y;
      area -= polygon[j].x * polygon[i].y;
    }
    
    return Math.abs(area) / 2;
  },

  calculatePolygonCenter(polygon) {
    let cx = 0, cy = 0;
    const n = polygon.length;
    
    for (let i = 0; i < n; i++) {
      cx += polygon[i].x;
      cy += polygon[i].y;
    }
    
    return {
      x: cx / n,
      y: cy / n
    };
  }
};